/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Box,
  Checkbox,
  Divider,
  Flex,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
} from '@tonic-one/react';
import isEqual from 'lodash/isEqual';
import React, { Key, memo } from 'react';
import { useTranslation } from 'react-i18next';
import CasTreeTransfer, {
  TransferLeftBox,
} from 'src/components/CasTreeTransfer';
import { FormLabel } from 'src/components/Common/FormComponent';
import EditableList from 'src/components/EditableList';
import { generalExport } from 'src/pages/Policy/utils/fns';
import { useWhyDidYouUpdate } from 'src/utils/hooks';
import config from 'src/config';

// import ImportModal from '../components/ImportModal';
import {
  storeSelector,
  templateSelector,
  usePolicyContext,
} from '@Policy/store';
import {
  fbBlockFileExtensionListSelector,
  fbBlockFileNameListSelector,
  fbBlockFileTypeListSelector,
  fbExceptionFileExtensionListSelector,
  fbExceptionFileNameListSelector,
  fbExceptionFileTypeListSelector,
  fbIncomingRuleBlockFileExtensionListSelector,
  fbIncomingRuleBlockFileNameListSelector,
  fbIncomingRuleBlockFileTypeListSelector,
} from '@Policy/store/filters/fileBlocking';
import { useRulesStyle } from './style';
import { ExchangeOnlinePolicyItem } from '@Policy/utils/types';
import ApplyBound from '../../Components/ApplyBound';
import ImportModal from '../../Components/ImportModal';
export const FbApplyBound = memo(() => {
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbApplyBound = policySelector((s) => s.tmFileblockings.fbApplyBound);
  const setFbState = contextSelector((s) => s.setFbState);
  // local state
  useWhyDidYouUpdate('Malware scanning ApplyBound', { fbApplyBound });
  return (
    <ApplyBound
      applyBoundValue={fbApplyBound}
      setApplyBound={setFbState}
      applyBoundKey="fbApplyBound"
    />
  );
});
export const FbEnabled = memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbEnabled = policySelector((s) => s.tmFileblockings.fbEnabled);
  const setFbState = contextSelector((s) => s.setFbState);
  return (
    <Flex alignItems="center" pt="8px" pb="16px">
      <Switch
        mr="1x"
        checked={!!fbEnabled}
        value={fbEnabled}
        onChange={() => {
          // setAsEnabled();
          setFbState({ fbEnabled: fbEnabled === 1 ? 0 : 1 });
        }}
      />
      <Text mr="8px" fontSize="18px" lineHeight="24px" ml={8}>
        {t('policy.enableFileBlocking')}
      </Text>
    </Flex>
  );
});
export const FileBlockingType = memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbScanningType = policySelector(
    (s) => s.tmFileblockings.fbScanningType
  );
  const setFbState = contextSelector((s) => s.setFbState);
  return (
    <Flex mt="12px" alignItems="flex-start">
      <FormLabel
        label={t('policy.enableFileBlocking.typeOfFileBlocking')}
        width="200px"
      />
      <RadioGroup
        value={fbScanningType}
        onChange={(value: number) =>
          setFbState({ fbScanningType: Number(value) })
        }
      >
        <Stack spacing="3x" shouldWrapChildren mt="6px">
          <Radio value={0}>
            <Text lineHeight="20px">
              {t('policy.enableFileBlocking.typeOfFileBlocking.allFiles')}
            </Text>
          </Radio>
          <Radio value={1}>
            {t('policy.enableFileBlocking.typeOfFileBlocking.specific.title')}
          </Radio>
        </Stack>
      </RadioGroup>
    </Flex>
  );
});
export const ExceptFileType = memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbExceptionFileTypeEnabled = policySelector(
    (s) => s.tmFileblockings.fbExceptionFileTypeEnabled
  );
  const fileTypes = contextSelector((s) => s.config.fileTypes);
  const exceptionFileTypes = contextSelector(
    fbExceptionFileTypeListSelector,
    isEqual
  );
  const setFbState = contextSelector((s) => s.setFbState);
  const setFbExceptionFileTypeList = contextSelector(
    (s) => s.setFbExceptionFileTypeList
  );
  const handleExceptionFileTypeListChange = React.useCallback((keys: Key[]) => {
    setFbExceptionFileTypeList(keys as string[]);
  }, []);
  return (
    <Stack gap="3x">
      <Flex alignItems="center">
        <Switch
          value={fbExceptionFileTypeEnabled}
          checked={!!fbExceptionFileTypeEnabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFbState({
              fbExceptionFileTypeEnabled: e.target.checked ? 1 : 0,
            });
          }}
          size="sm"
        />
        <Text fontSize="16px" lineHeight="22px" ml={8}>
          {t('policy.enableFileBlocking.typeOfFileNotBlocking.specific.type')}
        </Text>
      </Flex>
      {!!fbExceptionFileTypeEnabled && (
        <Box ml="11x">
          <CasTreeTransfer
            defaultValue={exceptionFileTypes}
            disabled={!fbExceptionFileTypeEnabled}
          >
            <TransferLeftBox
              title={t('policy.label.availableSenders')}
              treeData={fileTypes}
              handleChange={handleExceptionFileTypeListChange}
              placeholder={t('policy.placeholder.selectedTargets.items')}
            />
          </CasTreeTransfer>
        </Box>
      )}
    </Stack>
  );
});
export const ExceptFileExtension = memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbExceptionFileExtensionEnabled = policySelector(
    (s) => s.tmFileblockings.fbExceptionFileExtensionEnabled
  );
  const exceptionFileExts = contextSelector(
    fbExceptionFileExtensionListSelector,
    isEqual
  );
  const approvedFileExtensionCount = contextSelector(
    (s) => s.config.approvedFileExtensionCount
  );
  const setFbState = contextSelector((s) => s.setFbState);
  const addExceptionFileExts = contextSelector((s) => s.addExceptionFileExts);
  const removeExceptionFileExt = contextSelector(
    (s) => s.removeExceptionFileExt
  );
  // local state
  const [
    isExceptionFileExtsImportModalVisible,
    setIsExceptionFileExtsImportModalVisible,
  ] = React.useState<boolean>(false);
  // exception file exts
  // export
  const handleExceptionFileExtsExport = React.useCallback((entries: any) => {
    generalExport({
      entries: entries.map((entry: any) => entry.value),
      name: 'exceptionFileExts',
    });
  }, []);
  // import
  const handleExceptionFileExtsImportModalClose = React.useCallback(() => {
    setIsExceptionFileExtsImportModalVisible(false);
  }, []);
  const handleExceptionFileExtsImportModalOpen = React.useCallback(() => {
    setIsExceptionFileExtsImportModalVisible(true);
  }, []);
  const handleExceptionFileExtsImport = React.useCallback((entries: any) => {
    console.log('import entries', entries);
    addExceptionFileExts(entries as string[]);
    setIsExceptionFileExtsImportModalVisible(false);
  }, []);
  return (
    <>
      <Stack gap="3x">
        <Flex alignItems="center">
          <Switch
            value={fbExceptionFileExtensionEnabled}
            checked={!!fbExceptionFileExtensionEnabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFbState({
                fbExceptionFileExtensionEnabled: e.target.checked ? 1 : 0,
              });
            }}
            size="sm"
          />
          <Text fontSize="16px" lineHeight="22px" ml={8}>
            {t(
              'policy.enableFileBlocking.typeOfFileNotBlocking.specific.extensions'
            )}
          </Text>
        </Flex>
        {!!fbExceptionFileExtensionEnabled && (
          <Box ml="11x">
            <EditableList
              placeholder={t(
                'policy.malwareScanning.scanSelectedFile.customFileExtensions.content'
              )}
              onAdd={addExceptionFileExts}
              onDelete={removeExceptionFileExt}
              list={exceptionFileExts}
              limit={approvedFileExtensionCount}
              size={4}
              width="520px"
              disabled={!fbExceptionFileExtensionEnabled}
              description=""
              onExport={handleExceptionFileExtsExport}
              onImport={handleExceptionFileExtsImportModalOpen}
            />
          </Box>
        )}
      </Stack>
      <ImportModal
        isVisible={isExceptionFileExtsImportModalVisible}
        onClose={handleExceptionFileExtsImportModalClose}
        onImport={handleExceptionFileExtsImport}
      />
    </>
  );
});
export const ExceptFileName = memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbExceptionFileNameEnabled = policySelector(
    (s) => s.tmFileblockings.fbExceptionFileNameEnabled
  );
  const exceptionFileNames = contextSelector(
    fbExceptionFileNameListSelector,
    isEqual
  );
  const setFbState = contextSelector((s) => s.setFbState);
  const addExceptionFileNames = contextSelector((s) => s.addExceptionFileNames);
  const removeExceptionFileName = contextSelector(
    (s) => s.removeExceptionFileName
  );
  return (
    <Stack gap="3x">
      <Flex alignItems="center">
        <Switch
          value={fbExceptionFileNameEnabled}
          checked={!!fbExceptionFileNameEnabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFbState({
              fbExceptionFileNameEnabled: e.target.checked ? 1 : 0,
            });
          }}
          size="sm"
        />
        <Text fontSize="16px" lineHeight="22px" ml={8}>
          {t('policy.enableFileBlocking.typeOfFileNotBlocking.specific.names')}
        </Text>
      </Flex>
      {!!fbExceptionFileNameEnabled && (
        <Box ml="11x">
          <EditableList
            placeholder={t('policy.placeholder.addFileName')}
            onAdd={addExceptionFileNames}
            onDelete={removeExceptionFileName}
            list={exceptionFileNames}
            limit={-1}
            size={4}
            width="520px"
            disabled={!fbExceptionFileNameEnabled}
            description=""
          />
        </Box>
      )}
    </Stack>
  );
});
export const BlockFileType = memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbBlockFileTypeEnabled = policySelector(
    (s) => s.tmFileblockings.fbBlockFileTypeEnabled
  );
  const fileTypes = contextSelector((s) => s.config.fileTypes);
  const blockFileTypes = contextSelector(fbBlockFileTypeListSelector, isEqual);
  const setFbState = contextSelector((s) => s.setFbState);
  const setFbBlockFileTypeList = contextSelector(
    (s) => s.setFbBlockFileTypeList
  );
  // local state
  const handleBlockFileTypeListChange = React.useCallback((keys: Key[]) => {
    setFbBlockFileTypeList(keys as string[]);
  }, []);
  return (
    <Stack gap="3x">
      <Flex alignItems="center">
        <Switch
          value={fbBlockFileTypeEnabled}
          checked={!!fbBlockFileTypeEnabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFbState({
              fbBlockFileTypeEnabled: e.target.checked ? 1 : 0,
            });
          }}
          size="sm"
        />
        <Flex columnGap="2x" alignItems="center">
          <Text fontSize="16px" lineHeight="22px" ml={8}>
            {t('policy.enableFileBlocking.typeOfFileBlocking.specific.type')}
          </Text>
          {/* <Info
            content={t('policy.enableFileBlocking.fileTypeBlocking.limit', {
              LIMIT: config.ui.policy.limitation.fbFileMaxSize,
            })}
            placement="right-start"
          /> */}
        </Flex>
      </Flex>
      {!!fbBlockFileTypeEnabled && (
        <Box ml="11x">
          <CasTreeTransfer
            defaultValue={blockFileTypes}
            disabled={!fbBlockFileTypeEnabled}
          >
            <TransferLeftBox
              treeData={fileTypes}
              handleChange={handleBlockFileTypeListChange}
              placeholder={t('policy.placeholder.selectedTargets.items')}
            />
          </CasTreeTransfer>
        </Box>
      )}
    </Stack>
  );
});
export const BlockFileExtension = memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbBlockFileExtensionEnabled = policySelector(
    (s) => s.tmFileblockings.fbBlockFileExtensionEnabled
  );
  const blockFileExts = contextSelector(
    fbBlockFileExtensionListSelector,
    isEqual
  );
  const blockFileExtensionCount = contextSelector(
    (s) => s.config.blockFileExtensionCount
  );
  const setFbState = contextSelector((s) => s.setFbState);
  const addBlockFileExts = contextSelector((s) => s.addBlockFileExts);
  const removeBlockFileExt = contextSelector((s) => s.removeBlockFileExt);
  // local state
  const [
    isBlockFileExtsImportModalVisible,
    setIsBlockFileExtsImportModalVisible,
  ] = React.useState<boolean>(false);
  // block file exts
  // export
  const handleBlockFileExtsExport = React.useCallback((entries: any) => {
    generalExport({
      entries: entries.map((entry: any) => entry.value),
      name: 'blockFileExts',
    });
  }, []);
  // import
  const handleBlockFileExtsImportModalClose = React.useCallback(() => {
    setIsBlockFileExtsImportModalVisible(false);
  }, []);
  const handleBlockFileExtsImportModalOpen = React.useCallback(() => {
    setIsBlockFileExtsImportModalVisible(true);
  }, []);
  const handleBlockFileExtsImport = React.useCallback((entries: any) => {
    console.log('import entries', entries);
    addBlockFileExts(entries as string[]);
    setIsBlockFileExtsImportModalVisible(false);
  }, []);
  return (
    <Box>
      <Stack gap="3x">
        <Flex alignItems="center">
          <Switch
            value={fbBlockFileExtensionEnabled}
            checked={!!fbBlockFileExtensionEnabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFbState({
                fbBlockFileExtensionEnabled: e.target.checked ? 1 : 0,
              });
            }}
            size="sm"
          />
          <Text fontSize="16px" lineHeight="22px" ml={8}>
            {t(
              'policy.enableFileBlocking.typeOfFileBlocking.specific.extensions'
            )}
          </Text>
        </Flex>
        {!!fbBlockFileExtensionEnabled && (
          <Box ml="11x">
            <EditableList
              placeholder={t(
                'policy.malwareScanning.scanSelectedFile.customFileExtensions.content'
              )}
              onAdd={addBlockFileExts}
              onDelete={removeBlockFileExt}
              list={blockFileExts}
              limit={blockFileExtensionCount}
              size={4}
              width="520px"
              disabled={!fbBlockFileExtensionEnabled}
              description=""
              onExport={handleBlockFileExtsExport}
              onImport={handleBlockFileExtsImportModalOpen}
            />
          </Box>
        )}
      </Stack>
      <ImportModal
        isVisible={isBlockFileExtsImportModalVisible}
        onClose={handleBlockFileExtsImportModalClose}
        onImport={handleBlockFileExtsImport}
      />
    </Box>
  );
});
export const BlockFileName = memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbBlockFileNameEnabled = policySelector(
    (s) => s.tmFileblockings.fbBlockFileNameEnabled
  );
  const blockFileNames = contextSelector(fbBlockFileNameListSelector, isEqual);
  const setFbState = contextSelector((s) => s.setFbState);
  const addBlockFileNames = contextSelector((s) => s.addBlockFileNames);
  const removeBlockFileName = contextSelector((s) => s.removeBlockFileName);
  return (
    <Stack gap="3x">
      <Flex alignItems="center">
        <Switch
          value={fbBlockFileNameEnabled}
          checked={!!fbBlockFileNameEnabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFbState({
              fbBlockFileNameEnabled: e.target.checked ? 1 : 0,
            });
          }}
          size="sm"
        />
        <Text fontSize="16px" lineHeight="22px" ml={8}>
          {t('policy.enableFileBlocking.typeOfFileBlocking.specific.names')}
        </Text>
      </Flex>
      {!!fbBlockFileNameEnabled && (
        <Box ml="11x">
          <EditableList
            placeholder={t('policy.placeholder.addFileName')}
            onAdd={addBlockFileNames}
            onDelete={removeBlockFileName}
            list={blockFileNames}
            limit={-1}
            size={4}
            width="520px"
            disabled={!fbBlockFileNameEnabled}
            description=""
          />
        </Box>
      )}
    </Stack>
  );
});
export const AdditionalBlockFileType = memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbIncomingRuleBlockFileTypeEnabled = policySelector(
    (s) => s.tmFileblockings.fbIncomingRuleBlockFileTypeEnabled
  );
  const fbAdvancedIncomingRuleEnabled = policySelector(
    (s) => s.tmFileblockings.fbAdvancedIncomingRuleEnabled
  );
  const fileTypes = contextSelector((s) => s.config.fileTypes);
  const blockFileTypes = contextSelector(
    fbIncomingRuleBlockFileTypeListSelector,
    isEqual
  );
  const setFbState = contextSelector((s) => s.setFbState);
  const setFbIncomingRuleBlockFileTypeList = contextSelector(
    (s) => s.setFbIncomingRuleBlockFileTypeList
  );
  // local state
  const handleBlockFileTypeListChange = React.useCallback((keys: Key[]) => {
    setFbIncomingRuleBlockFileTypeList(keys as string[]);
  }, []);
  return (
    <Stack gap="3x">
      <Flex alignItems="center">
        <Checkbox
          checked={!!fbIncomingRuleBlockFileTypeEnabled}
          disabled={!fbAdvancedIncomingRuleEnabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFbState({
              fbIncomingRuleBlockFileTypeEnabled: e.target.checked ? 1 : 0,
            });
          }}
        >
          <Flex columnGap="2x" alignItems="center">
            <Text fontSize="16px" lineHeight="22px">
              {t('policy.enableFileBlocking.typeOfFileBlocking.specific.type')}
            </Text>
            {/* <Info
              content={t('policy.enableFileBlocking.fileTypeBlocking.limit', {
                LIMIT: config.ui.policy.limitation.fbFileMaxSize,
              })}
              placement="right-start"
            /> */}
          </Flex>
        </Checkbox>
      </Flex>
      {fbIncomingRuleBlockFileTypeEnabled === 1 ? (
        <CasTreeTransfer
          defaultValue={blockFileTypes}
          disabled={!fbAdvancedIncomingRuleEnabled}
        >
          <TransferLeftBox
            treeData={fileTypes}
            handleChange={handleBlockFileTypeListChange}
            placeholder={t('policy.placeholder.selectedTargets.items')}
          />
        </CasTreeTransfer>
      ) : null}
    </Stack>
  );
});
export const AdditionalBlockFileExtension = memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbIncomingRuleBlockFileExtensionEnabled = policySelector(
    (s) => s.tmFileblockings.fbIncomingRuleBlockFileExtensionEnabled
  );
  const fbAdvancedIncomingRuleEnabled = policySelector(
    (s) => s.tmFileblockings.fbAdvancedIncomingRuleEnabled
  );
  const blockFileExts = contextSelector(
    fbIncomingRuleBlockFileExtensionListSelector,
    isEqual
  );
  const blockFileExtensionCount = contextSelector(
    (s) => s.config.blockFileExtensionCount
  );
  const setFbState = contextSelector((s) => s.setFbState);
  const addBlockFileExts = contextSelector((s) => s.addAdditionalBlockFileExts);
  const removeBlockFileExt = contextSelector(
    (s) => s.removeAdditionalBlockFileExt
  );
  // local state
  const [
    isBlockFileExtsImportModalVisible,
    setIsBlockFileExtsImportModalVisible,
  ] = React.useState<boolean>(false);
  // block file exts
  // export
  const handleBlockFileExtsExport = React.useCallback((entries: any) => {
    generalExport({
      entries: entries.map((entry: any) => entry.value),
      name: 'addtional-blockFileExts',
    });
  }, []);
  // import
  const handleBlockFileExtsImportModalClose = React.useCallback(() => {
    setIsBlockFileExtsImportModalVisible(false);
  }, []);
  const handleBlockFileExtsImportModalOpen = React.useCallback(() => {
    setIsBlockFileExtsImportModalVisible(true);
  }, []);
  const handleBlockFileExtsImport = React.useCallback((entries: any) => {
    console.log('import entries', entries);
    addBlockFileExts(entries as string[]);
    setIsBlockFileExtsImportModalVisible(false);
  }, []);
  return (
    <Box>
      <Stack gap="3x">
        <Flex alignItems="center">
          <Checkbox
            checked={!!fbIncomingRuleBlockFileExtensionEnabled}
            disabled={!fbAdvancedIncomingRuleEnabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              console.log(e.target.checked);
              setFbState({
                fbIncomingRuleBlockFileExtensionEnabled: e.target.checked
                  ? 1
                  : 0,
              });
            }}
          >
            <Text fontSize="16px" lineHeight="22px">
              {t(
                'policy.enableFileBlocking.typeOfFileBlocking.specific.extensions'
              )}
            </Text>
          </Checkbox>
        </Flex>
        {fbIncomingRuleBlockFileExtensionEnabled === 1 ? (
          <EditableList
            placeholder={t(
              'policy.malwareScanning.scanSelectedFile.customFileExtensions.content'
            )}
            onAdd={addBlockFileExts}
            onDelete={removeBlockFileExt}
            list={blockFileExts}
            limit={blockFileExtensionCount}
            size={4}
            width="520px"
            disabled={!fbAdvancedIncomingRuleEnabled}
            description=""
            onExport={handleBlockFileExtsExport}
            onImport={handleBlockFileExtsImportModalOpen}
          />
        ) : null}
      </Stack>
      <ImportModal
        isVisible={isBlockFileExtsImportModalVisible}
        onClose={handleBlockFileExtsImportModalClose}
        onImport={handleBlockFileExtsImport}
      />
    </Box>
  );
});
export const AdditionalBlockFileName = memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbIncomingRuleBlockFileNameEnabled = policySelector(
    (s) => s.tmFileblockings.fbIncomingRuleBlockFileNameEnabled
  );
  const fbAdvancedIncomingRuleEnabled = policySelector(
    (s) => s.tmFileblockings.fbAdvancedIncomingRuleEnabled
  );
  const blockFileNames = contextSelector(
    fbIncomingRuleBlockFileNameListSelector,
    isEqual
  );
  const setFbState = contextSelector((s) => s.setFbState);
  const addBlockFileNames = contextSelector(
    (s) => s.addAdditionalBlockFileNames
  );
  const removeBlockFileName = contextSelector(
    (s) => s.removeAddtionalBlockFileName
  );
  return (
    <Stack gap="3x">
      <Flex alignItems="center">
        <Checkbox
          checked={!!fbIncomingRuleBlockFileNameEnabled}
          disabled={!fbAdvancedIncomingRuleEnabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            console.log(e.target.checked);
            setFbState({
              fbIncomingRuleBlockFileNameEnabled: e.target.checked ? 1 : 0,
            });
          }}
        >
          <Text fontSize="16px" lineHeight="22px">
            {t('policy.enableFileBlocking.typeOfFileBlocking.specific.names')}
          </Text>
        </Checkbox>
      </Flex>
      {fbIncomingRuleBlockFileNameEnabled === 1 ? (
        <EditableList
          placeholder={t('policy.placeholder.addFileName')}
          onAdd={addBlockFileNames}
          onDelete={removeBlockFileName}
          list={blockFileNames}
          limit={-1}
          size={4}
          width="520px"
          disabled={!fbAdvancedIncomingRuleEnabled}
          description=""
        />
      ) : null}
    </Stack>
  );
});
export const BlockAdditionalFile = memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbApplyBound = policySelector((s) => s.tmFileblockings.fbApplyBound);
  const fbAdvancedIncomingRuleEnabled = policySelector(
    (s) => s.tmFileblockings.fbAdvancedIncomingRuleEnabled
  );
  const setFbState = contextSelector((s) => s.setFbState);
  return (
    <>
      {fbApplyBound === 7 && (
        <Stack gap="3x">
          <Flex alignItems="center">
            <Switch
              value={fbAdvancedIncomingRuleEnabled}
              checked={!!fbAdvancedIncomingRuleEnabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFbState({
                  fbAdvancedIncomingRuleEnabled: e.target.checked ? 1 : 0,
                });
              }}
              size="sm"
            />
            <Text fontSize="16px" lineHeight="22px" ml={8}>
              {t(
                'policy.enableFileBlocking.typeOfFileBlocking.specific.additionalFiles'
              )}
            </Text>
          </Flex>
          <Stack gap="3x" ml="11x">
            <AdditionalBlockFileType />
            <AdditionalBlockFileExtension />
            <AdditionalBlockFileName />
          </Stack>
        </Stack>
      )}
    </>
  );
});
export const CompressedFiles = memo(() => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbCompressedFileScanningType = policySelector(
    (s) => s.tmFileblockings.fbCompressedFileScanningType
  );
  const setFbState = contextSelector((s) => s.setFbState);
  return (
    <Flex mt="16px" alignItems="flex-start">
      <FormLabel
        label={t('policy.enableFileBlocking.compressedFile')}
        width="200px"
      />
      <Checkbox
        checked={!!fbCompressedFileScanningType}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          console.log(e.target.checked);
          setFbState({
            fbCompressedFileScanningType: e.target.checked ? 1 : 0,
          });
        }}
      >
        <Text fontSize="16px" lineHeight="22px">
          {t('policy.enableFileBlocking.compressedFile.desc')}
        </Text>
      </Checkbox>
    </Flex>
  );
});

function Rules({
  applyBoundShow,
  blockAdditionalFileShow,
}: {
  applyBoundShow: boolean;
  blockAdditionalFileShow: boolean;
}) {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state and action from store
  const fbScanningType = policySelector(
    (s) => s.tmFileblockings.fbScanningType
  );
  useWhyDidYouUpdate('FileBlocking rules', {
    fbScanningType,
  });
  return (
    <div>
      <Box ml="1px" mt="16px">
        <FbEnabled />
        <Divider mt="10px" mb="10px" />
        {applyBoundShow ? <FbApplyBound /> : null}
        <FileBlockingType />
        {fbScanningType === 0 && (
          <Flex mt="16px" alignItems="flex-start">
            <FormLabel
              label={t(
                'policy.enableFileBlocking.typeOfFileBlocking.specific.exceptionList'
              )}
              width="200px"
            />
            <Stack gap="3x">
              <ExceptFileType />
              <ExceptFileExtension />
              <ExceptFileName />
            </Stack>
          </Flex>
        )}
        {fbScanningType === 1 && (
          <Flex mt="16px" alignItems="flex-start">
            <FormLabel
              label={t(
                'policy.enableFileBlocking.typeOfFileBlocking.specific.blockingList'
              )}
              width="200px"
            />
            <Stack gap="3x">
              <BlockFileType />
              <BlockFileExtension />
              <BlockFileName />
              {blockAdditionalFileShow ? <BlockAdditionalFile /> : null}
            </Stack>
          </Flex>
        )}
        <Divider mt="32px" mb="32px" />
        <CompressedFiles />
      </Box>
    </div>
  );
}

export default React.memo(Rules);
